
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { SettingsMenu } from "@/components/SettingsMenu";
import { Card } from "@/components/ui/card";
import { PayoutSettings } from "@/components/settings/PayoutSettings";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { CreatorPaymentSettings } from "@/components/profile/CreatorPaymentSettings";

export default function Settings() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isCreator, setIsCreator] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [payoutEmail, setPayoutEmail] = useState("");
  const [payoutMethod, setPayoutMethod] = useState<string>("");
  const [earnings, setEarnings] = useState<number>(0);
  const [lastPayoutDate, setLastPayoutDate] = useState<string | null>(null);
  const [bankDetails, setBankDetails] = useState({
    beneficiaryName: "",
    bankName: "",
    routingNumber: "",
    accountNumber: "",
    otherDetails: "",
  });
  const [userId, setUserId] = useState<string>("");

  useEffect(() => {
    checkAuth();
    fetchUserProfile();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        return;
      }
    } catch (error) {
      console.error("Auth error:", error);
      toast.error("Authentication error. Please log in again.");
      navigate("/login");
    }
  };

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/login");
        return;
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*, creator_status, payout_email, payout_method, earnings, last_payout_date, bank_beneficiary_name, bank_name, bank_routing_number, bank_account_number, bank_other_details, is_superuser')
        .eq('id', user.id)
        .single();

      if (error) {
        throw error;
      }

      if (profile) {
        setIsCreator(profile.creator_status === 'creator');
        setIsSuperuser(profile.is_superuser);
        setPayoutEmail(profile.payout_email || '');
        setPayoutMethod(profile.payout_method || '');
        setEarnings(profile.earnings || 0);
        setLastPayoutDate(profile.last_payout_date);
        setBankDetails({
          beneficiaryName: profile.bank_beneficiary_name || '',
          bankName: profile.bank_name || '',
          routingNumber: profile.bank_routing_number || '',
          accountNumber: profile.bank_account_number || '',
          otherDetails: profile.bank_other_details || '',
        });
        setUserId(user.id);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error("Failed to load profile data");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayoutSettingsUpdate = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please log in again");
        return;
      }

      const updates = {
        payout_email: payoutEmail,
        payout_method: payoutMethod,
      };

      if (payoutMethod === 'bank_transfer') {
        Object.assign(updates, {
          bank_beneficiary_name: bankDetails.beneficiaryName,
          bank_name: bankDetails.bankName,
          bank_routing_number: bankDetails.routingNumber,
          bank_account_number: bankDetails.accountNumber,
          bank_other_details: bankDetails.otherDetails,
        });
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      toast.success("Payout settings updated successfully");
    } catch (error) {
      console.error("Error updating payout settings:", error);
      toast.error("Failed to update payout settings");
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col">
          <Header />
          <main className="flex-1">
            <div className="container max-w-4xl mx-auto py-12 px-4 space-y-8">
              <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold">Settings</h1>
              </div>

              <Card className="p-6">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium">App Settings</h3>
                      <p className="text-sm text-gray-500">
                        Manage your application preferences
                      </p>
                    </div>
                    <SettingsMenu />
                  </div>
                </div>
              </Card>

              {isCreator && (
                <>
                  <CreatorPaymentSettings userId={userId} />
                  <Card className="p-6">
                    <PayoutSettings
                      earnings={earnings}
                      lastPayoutDate={lastPayoutDate}
                      payoutEmail={payoutEmail}
                      setPayoutEmail={setPayoutEmail}
                      payoutMethod={payoutMethod}
                      setPayoutMethod={setPayoutMethod}
                      bankDetails={bankDetails}
                      setBankDetails={setBankDetails}
                      onSave={handlePayoutSettingsUpdate}
                    />
                  </Card>
                </>
              )}
            </div>
          </main>
          <Footer />
        </div>
      </div>
    </SidebarProvider>
  );
}
